/**
 * Source API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  Source,
  SourceListResponse,
  SourceResponse,
  SourceCreateInput,
  SourceUpdateInput,
} from '../types/source';

interface ListSourcesOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class SourceService {
  /**
   * List sources with pagination.
   */
  async listSources(
    workspaceId: string,
    idToken: string,
    options: ListSourcesOptions = {}
  ): Promise<SourceListResponse> {
    const { page = 1, size = 50, sortBy = 'name', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<SourceListResponse>(`/sources`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list sources');
    }

    return response.data;
  }

  /**
   * Get a single source by ID.
   */
  async getSource(workspaceId: string, sourceId: string, idToken: string): Promise<Source> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<SourceResponse>(`/sources/${sourceId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get source');
    }

    return response.data.data;
  }

  /**
   * Create a new source.
   */
  async createSource(workspaceId: string, idToken: string, data: SourceCreateInput): Promise<Source> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<SourceResponse>(`/sources`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create source');
    }

    return response.data.data;
  }

  /**
   * Bulk create sources; duplicates skipped silently.
   */
  async bulkCreateSources(
    workspaceId: string,
    idToken: string,
    names: readonly string[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/sources/bulk`,
      { names },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create sources');
    }

    return response.data.data;
  }

  /**
   * Update a source.
   */
  async updateSource(
    workspaceId: string,
    sourceId: string,
    idToken: string,
    data: SourceUpdateInput
  ): Promise<Source> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<SourceResponse>(`/sources/${sourceId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update source');
    }

    return response.data.data;
  }

  /**
   * Delete a source.
   */
  async deleteSource(workspaceId: string, sourceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/sources/${sourceId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const sourceService = new SourceService();
export default sourceService;
